import { useEffect, useState } from "react"

import {
  getAllMarketPricing,
  getMarketPricingBySKUs,
  VariantWithMarketPricing,
} from "../utils/marketPricingLoader"

interface UseMarketPricingResult {
  data: Map<string, VariantWithMarketPricing> | VariantWithMarketPricing[]
  error: Error | null
  loading: boolean
}

/**
 * Hook to fetch ALL market pricing data
 * Use sparingly - loads all chunks. Prefer useMarketPricing with specific SKUs.
 *
 * @returns Object with data (array of all variants), loading state, and error
 *
 * @example
 * ```tsx
 * const { data, loading, error } = useAllMarketPricing()
 *
 * if (loading) return <div>Loading...</div>
 * if (error) return <div>Error loading pricing</div>
 *
 * // data is an array of all variants
 * const variant = data.find(v => v.sku === 'SKU123')
 * ```
 */
export function useAllMarketPricing(): UseMarketPricingResult {
  const [data, setData] = useState<VariantWithMarketPricing[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    let isMounted = true

    const fetchData = async () => {
      try {
        setLoading(true)
        setError(null)

        const pricingData = await getAllMarketPricing()

        if (isMounted) {
          setData(pricingData)
          setLoading(false)
        }
      } catch (err) {
        if (isMounted) {
          setError(err as Error)
          setLoading(false)
        }
      }
    }

    fetchData()

    return () => {
      isMounted = false
    }
  }, [])

  return { data, error, loading }
}

/**
 * Hook to fetch market pricing data for specific SKUs
 *
 * @param skus - Array of SKUs to fetch pricing for
 * @returns Object with data (Map<SKU, variant>), loading state, and error
 *
 * @example
 * ```tsx
 * const { data, loading, error } = useMarketPricing(['SKU123', 'SKU456'])
 *
 * if (loading) return <div>Loading...</div>
 * if (error) return <div>Error loading pricing</div>
 *
 * const variant = data.get('SKU123')
 * const price = variant.marketPrices.US.price.amount
 * ```
 */
export function useMarketPricing(skus: string[]): UseMarketPricingResult {
  const [data, setData] = useState<Map<string, VariantWithMarketPricing>>(
    new Map()
  )
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!skus || skus.length === 0) {
      setData(new Map())
      setLoading(false)
      return
    }

    let isMounted = true

    const fetchData = async () => {
      try {
        setLoading(true)
        setError(null)

        const pricingData = await getMarketPricingBySKUs(skus)

        if (isMounted) {
          setData(pricingData)
          setLoading(false)
        }
      } catch (err) {
        if (isMounted) {
          setError(err as Error)
          setLoading(false)
        }
      }
    }

    fetchData()

    return () => {
      isMounted = false
    }
  }, [JSON.stringify(skus)]) // Use JSON.stringify for stable array comparison

  return { data, error, loading }
}
